import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';

import { trackDemoNotificationToggle } from '../../../analytics';

import { ChangingTip } from '../../../components/custom';
import { Spinner, Checkbox, ProgressBar } from '../../../components/general';

import useDemoData from '../../../hooks/useDemoData';

import { getRandomPercent } from '../../../utils';

interface Props {
  type?: 'analysis' | 'generation';
  heading: string;
  tipsList: string[];
  expectedMin: number;
}

const DemoLoading: React.FC<Props> = (props) => {
  const { type, heading, tipsList, expectedMin } = props;

  const { demoData, updateDemoData } = useDemoData();

  const [progress, setProgress] = useState(0);
  const [remainingMin, setRemainingMin] = useState(expectedMin);
  const [endTime, setEndTime] = useState(Date.now() + expectedMin * 60 * 1000);

  const startTime = useMemo(() => Date.now(), []);
  const total = useMemo(() => endTime - startTime, [startTime, endTime]);

  const [isEmailChecked, setIsEmailChecked] = useState(
    type ? demoData.notifications[type] : true,
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - startTime;
      const currentProgress = Math.round(Math.min((elapsed / total) * 100, 99));
      const timeLeft = endTime - now;

      setProgress(currentProgress);

      const remaining = Math.max(timeLeft / 60 / 1000, 0);
      setRemainingMin(remaining);

      // check if we should extend (when less than 10-30% time left)
      const threshold = total * getRandomPercent(10, 30);

      // 20% chance to extend when under threshold
      const chance = Math.random() < 0.2;

      if ((timeLeft < threshold && chance) || progress >= 95)
        // random extension: 5-25% of original expected time
        setEndTime((prev) => prev + getRandomPercent(5, 25) * 60 * 1000);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, endTime, total, progress]);

  const toggleEmailCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!type) return;

    const newValue = e.target.checked;

    setIsEmailChecked(newValue);

    updateDemoData({
      ...demoData,
      notifications: { ...demoData.notifications, [type]: newValue },
    });

    trackDemoNotificationToggle(type, newValue);

    toast.success(
      <>
        <b>Preferences updated</b>: you {newValue ? 'will' : "won't"} receive an
        email when your {type} is done.
      </>,
    );
  };

  return (
    <React.Fragment>
      <Spinner
        customSize="w-20 h-20"
        color="primary"
        className="pt-8 sm:pt-16"
      />

      <h4>{heading}</h4>

      <div>
        <b>Keep this page open while we process your videos.</b> You can switch
        tabs and come back later, but please don’t close this page.
      </div>

      {type && (
        <Checkbox
          label={`Email me when the ${type} is complete`}
          name={`demo-loading-${type}-notification`}
          checked={isEmailChecked}
          onChange={toggleEmailCheck}
        />
      )}

      <ProgressBar
        progress={progress}
        estTime={Math.ceil(remainingMin)}
        isThin
        isBoxed
      />

      <ChangingTip tipsList={tipsList} />

      <hr className="my-4" />

      <div className="text-sm italic">
        Contact us at{' '}
        <a href="mailto:hello@crowdclip.com">hello@crowdclip.com</a> for further
        assistance.
      </div>
    </React.Fragment>
  );
};

export default DemoLoading;
